/**
 * The fan's loyalty session, in the shape the UI library takes: a live bearer, and their club.
 *
 * Both come from `window.urcAuth` and from the club table the page publishes. **Nothing here reads
 * a cookie**, and that is the whole of what this module used to be.
 *
 * WHAT WENT, AND WHY NONE OF IT IS NEEDED ANY MORE
 * -----------------------------------------------
 * This file was a cookie parser with three layers of defence around it, and every layer existed for
 * one reason: a third-party inline script expires every `auth0_*` cookie on `DOMContentLoaded`, on
 * every page of the site, and since [D-063](../../../../docs/decisions.md) dropped HttpOnly so this
 * module could read the token, that loop reached the credential too. So there was:
 *
 * - `pickToken`, `claims`, `isOurs` and `secondsLeft` — validating a JWT out of a cookie, because a
 *   cookie is client input wherever it is read. The SDK validates the token's signature before its
 *   claims are exposed, and owns its own expiry.
 * - `captureSession()` — holding the credential the page loaded with, because the jar would be
 *   emptied a moment later. There is no jar.
 * - `loggedOut()` — the `auth0_logged_out` tombstone, so a captured credential could not outlive a
 *   sign-out in another tab. The SDK's own logout clears its cache and the cross-tab channel in
 *   `resources/scripts/auth/index.js` re-asks it, so there is no list of cookies to keep in step
 *   and no tombstone to expire.
 * - `pinCredential()` — holding a credential above the tombstone for the length of one write, so
 *   the silent probe could not destroy a live session mid-click ([D-074](../../../../docs/decisions.md)).
 *   Nothing writes a tombstone and no probe clears anything, so there is nothing to hold it above.
 * - `pickTeam` — matching the `auth0_team_id` cookie against the club table. Replaced by
 *   `clubBranding()` below, which does the same lookup against `AuthState.clubId`.
 *
 * WHAT `authenticated` MEANS, WHICH HAS NOT CHANGED
 * ------------------------------------------------
 * "There is a live loyalty-audience bearer for this fan" — narrower than "the fan is signed in",
 * and still the question any surface offering an ACTION has to ask. The two can differ: a fan can
 * resolve perfectly while the audience is refused, which is [D-046](../../../../docs/decisions.md)'s
 * split state, and `resources/scripts/auth/runtime.js` warns when it happens rather than reporting
 * it as a clean sign-out. For "is this fan signed in", read `./urcAuth.js`.
 *
 * The club table is `window.urcLoyalty.teams`, every club rather than the fan's, because block
 * markup is edge-cached and a fan-specific row baked into it is served to the next visitor —
 * `App\Loyalty\FanTeam`'s docblock carries that argument and it is unaffected by any of this.
 */

// Imported with its extension: this module is covered by session.test.js under `node --test`,
// which does no bundler resolution. Vite resolves the explicit form too. Same reason
// ./toasts/enrolled.js spells out its own import.
import { accessToken, authReady, authState } from './urcAuth.js';

/**
 * Config emitted by `App\Loyalty\LoyaltyAssets`.
 *
 * Guarded for a non-browser global so this module can be imported under `node --test`.
 */
const config = () => ( typeof window === 'undefined' ? {} : window.urcLoyalty || {} );

/** The shape every reader below degrades to. Never a rejected promise, never a thrown error. */
const NO_SESSION = Object.freeze( { authenticated: false, token: '', team: null } );

/**
 * The fan's club branding row, or null.
 *
 * Null is a supported state on the library side — `LoyaltyHero` draws an unbranded band for it —
 * and it covers three cases that need no distinguishing: no fan, a fan who has selected no club,
 * and a club the table does not carry.
 *
 * **Compared as strings, deliberately.** `AuthState.clubId` is a number (`auth/state.js` parses the
 * `team_name` claim strictly and answers `null` for anything that is not a positive integer), while
 * `FanTeam::row()` sends `teamId` as a string because `wp_localize_script` would otherwise encode
 * the table as a JSON object or an array depending on whether the ids happen to form a list.
 * Coercing one side is what keeps that a property of the data rather than of the contract.
 *
 * @param {number|null|undefined} clubId The fan's club id.
 * @param {Object[]}              teams  The club table from the page.
 * @return {Object|null} The branding row, or null.
 */
export function clubBranding( clubId, teams ) {
	if ( ! clubId || ! Array.isArray( teams ) ) {
		return null;
	}

	const wanted = String( clubId );

	return teams.find( row => row && String( row.teamId ) === wanted ) || null;
}

/**
 * Assemble the session from an AuthState and a bearer. Pure.
 *
 * `authenticated` follows the TOKEN and not `state.signedIn`, which is the distinction the module
 * docblock describes: a fan the SDK resolved but for whom the loyalty audience was refused is
 * signed in and has no usable session, and every self-scoped read has to refuse rather than be sent.
 *
 * @param {Object|null} state An AuthState, or null.
 * @param {string}      token The bearer, or ''.
 * @param {Object[]}    teams The club table.
 * @return {{authenticated: boolean, token: string, team: Object|null}} The session.
 */
export function readSession( state, token, teams ) {
	// The club is resolved either way. It is not the credential, and a signed-out visitor's hero
	// still has a band to draw -- see the join hero, which shows the tier ladder to anybody.
	const team = clubBranding( state && state.clubId, teams );

	return typeof token === 'string' && token !== ''
		? { authenticated: true, token, team }
		: { ...NO_SESSION, team };
}

/**
 * The session, once the SDK has settled.
 *
 * **`ready`-backed rather than read off `state()`, and that is load-bearing.** `state()` answers
 * `null` until the SDK settles, and `null` is not "signed out" — a `state()`-backed read would
 * collapse "still resolving" and "answered, no club" into one value. `useLoyaltySession` is
 * tri-state precisely to tell those apart: `ProfileHeaderBlock` renders no hero until the answer
 * lands, because flashing an unbranded band before the fan's club arrives is the defect that branch
 * exists to prevent.
 *
 * Never rejects. `./urcAuth.js` degrades every failure to "nobody", so a broken tenant produces a
 * signed-out session rather than an unhandled rejection in a Preact effect.
 *
 * @return {Promise<{authenticated: boolean, token: string, team: Object|null}>} The session.
 */
export function getSession() {
	return authReady().then( state =>
		accessToken().then( token => readSession( state, token, config().teams ) )
	);
}

/**
 * The session, read again.
 *
 * Identical to `getSession()` and kept as its own name on purpose: the callers that reach for it
 * (`useLoyaltySession.js`, `toasts/gate.js`, `toasts/trigger.js`) are expressing "ask again, the
 * answer may have changed", which is worth being able to read in the code. The SDK answers a warm
 * cache from memory, so a re-ask costs nothing on an ordinary page.
 *
 * @return {Promise<{authenticated: boolean, token: string, team: Object|null}>} The session.
 */
export function refreshSession() {
	return getSession();
}

/**
 * A JWT's payload, or null when the value is not a readable JWT.
 *
 * **The last of the cookie-era JWT handling, and it is on its way out.** Nothing here decides
 * anything: the signature is unverified, and the loyalty API verifies RS256 against the tenant
 * JWKS on every request. Its one remaining caller is `toasts/enrolled.js`, which takes the `sub`
 * out of the bearer to key its cached enrolment verdict — a value now available directly as
 * `authState().sub`, from a token the SDK has already validated. It goes when that moves.
 *
 * @param {string} token The raw token.
 * @return {Object|null} The claims, or null.
 */
export function claims( token ) {
	const parts = typeof token === 'string' ? token.split( '.' ) : [];

	if ( parts.length !== 3 ) {
		return null;
	}

	try {
		const base64 = parts[ 1 ].replace( /-/g, '+' ).replace( /_/g, '/' );
		const padded = base64.padEnd( Math.ceil( base64.length / 4 ) * 4, '=' );
		const binary = atob( padded );
		const bytes = Uint8Array.from( binary, character => character.charCodeAt( 0 ) );
		const payload = JSON.parse( new TextDecoder().decode( bytes ) );

		return payload && typeof payload === 'object' ? payload : null;
	} catch ( error ) {
		return null;
	}
}

/**
 * The fan's club, straight off the settled state. Synchronous, and `null` before the SDK answers.
 *
 * For a caller that wants the branding without the credential — the crest is not a secret and does
 * not need a token. `null` covers "not answered yet" as well as "no club", which is right here:
 * both mean "draw the unbranded band for now".
 *
 * @return {Object|null} The branding row, or null.
 */
export function currentTeam() {
	const state = authState();

	return clubBranding( state && state.clubId, config().teams );
}
